import { z } from 'zod';
import { authMethodSchema, userRoleSchema } from './auth.js';
import { identifierSchema, safeText } from './common.js';
/**
 * The admin area's contract.
 *
 * Three groups of endpoints with one thing in common: everything here is behind
 * `requireRole('admin')`, so none of these schemas needs to worry about what a member may see.
 * What they do worry about is the opposite — that an *admin* screen still cannot become a way to
 * read something the system does not otherwise expose. Two consequences worth stating:
 *
 * - **No endpoint here returns transcript text, a password hash, a session id or an invite
 *   token.** A response schema is also Fastify's serialization filter, so a column added to
 *   `users` or `search_queries` later cannot leak through a screen that was never meant to show
 *   it.
 * - **The query log is returned with the question in it, because the question is the point** —
 *   the zero-result list is the single most useful thing in the admin area — but never with the
 *   answer, the retrieved snippets or the caller's IP. The first two are reproducible from the
 *   ids; the third is in the database for abuse investigation and does not belong on a dashboard.
 *
 * Every list here paginates **server-side**. A member table that is fetched whole and filtered in
 * the browser is a table that works until the tenth customer onboards.
 */
/* -- Users -------------------------------------------------------------------------------- */
/**
 * `limit`/`offset` rather than a cursor.
 *
 * The admin tables are sorted on columns a person chooses — email, last login — and jumping to
 * page 7 of a filtered list is a thing admins actually do. Both are cheap at this scale: the
 * user table is in the hundreds of rows, not the millions.
 */
export const ADMIN_PAGE_SIZE = 25;
export const ADMIN_MAX_PAGE_SIZE = 100;
const adminPaginationShape = {
    limit: z.coerce.number().int().min(1).max(ADMIN_MAX_PAGE_SIZE).default(ADMIN_PAGE_SIZE),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
};
/** Active or deactivated. Not a column — `users.is_active` — but the word an admin uses. */
export const userStatusSchema = z.enum(['active', 'inactive']);
export const USER_SORT_KEYS = ['email', 'createdAt', 'lastLoginAt', 'role'];
export const userSortSchema = z.enum(USER_SORT_KEYS);
export const adminUserListQuerySchema = z.object({
    ...adminPaginationShape,
    /**
     * Substring match over email and display name.
     *
     * Optional and, when present, non-empty: the client drops a cleared box rather than sending
     * `?q=`, which every `safeText` endpoint rejects as a validation failure.
     */
    q: safeText(200, 'alex').optional(),
    role: userRoleSchema.optional(),
    method: authMethodSchema.optional(),
    status: userStatusSchema.optional(),
    sort: userSortSchema.default('email'),
    direction: z.enum(['asc', 'desc']).default('asc'),
});
/**
 * A user as the admin table shows them.
 *
 * `authMethods` is derived from the presence of rows in `identities` and `credentials` rather
 * than stored, which is why an invited external account that is later federated correctly shows
 * both. `sessionCount` is what makes "force sign-out" legible: an admin can see whether the
 * action did anything.
 */
export const adminUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string().nullable(),
    role: userRoleSchema,
    isActive: z.boolean(),
    authMethods: z.array(authMethodSchema),
    lastLoginAt: z.string().nullable().describe('ISO 8601; null if they have never signed in'),
    createdAt: z.string().describe('ISO 8601'),
    sessionCount: z.number().int().nonnegative().describe('Live sessions right now'),
});
export const adminUserListResponseSchema = z.object({
    items: z.array(adminUserSchema),
    /**
     * An honest count of the *filtered* set.
     *
     * Unlike lesson search, this is a plain relational query over a few hundred rows, so a total
     * is both cheap and true — and a page-number paginator needs one.
     */
    total: z.number().int().nonnegative(),
    limit: z.number().int(),
    offset: z.number().int(),
});
/**
 * A user update: role, activation, or both.
 *
 * `.refine` rather than two endpoints, because both fields are privilege changes with the same
 * consequence — every session for that account is destroyed — and splitting them would mean two
 * handlers that each have to remember to do that. An empty body is rejected: a PATCH that asks
 * for nothing is a client bug, and answering 200 to it hides the bug.
 *
 * There is deliberately **no** field for a hard delete. These records carry audit meaning:
 * `invites.invited_by` points at them, and `search_queries.user_id` is how an evaluation set is
 * attributed. Deactivation is the reversible operation that means what an admin wants.
 */
export const adminUpdateUserRequestSchema = z
    .object({
    role: userRoleSchema.optional(),
    isActive: z.boolean().optional(),
})
    .refine((body) => body.role !== undefined || body.isActive !== undefined, {
    message: 'Supply at least one of role or isActive',
});
export const adminUserParamSchema = z.object({ id: identifierSchema });
/** The result of revoking someone's sessions, so the UI can say what happened. */
export const revokeSessionsResponseSchema = z.object({
    ok: z.literal(true),
    revoked: z.number().int().nonnegative(),
});
/* -- Content and ingest status ------------------------------------------------------------ */
/**
 * A coverage figure: how many rows of a kind exist, and how many have the thing in question.
 *
 * `total` travels with every count deliberately. "117 lessons have summaries" is not actionable;
 * "117 of 1,743" is. The UI renders the pair and never computes a percentage from two separate
 * fields that might have been measured at different times.
 */
export const coverageSchema = z.object({
    total: z.number().int().nonnegative(),
    covered: z.number().int().nonnegative(),
});
export const contentStatsSchema = z.object({
    courses: z.object({
        total: z.number().int().nonnegative(),
        /** `has_transcripts`, which prompt 04 clears for the 5 folders that turned out to be empty. */
        withTranscripts: z.number().int().nonnegative(),
        withoutTranscripts: z.number().int().nonnegative(),
        summary: coverageSchema,
        audience: coverageSchema,
        outcomes: coverageSchema,
        topics: coverageSchema,
        /** A row in `course_embeddings`, which is the coarse retrieval tier. */
        embedded: coverageSchema,
    }),
    modules: z.object({
        total: z.number().int().nonnegative(),
        titled: coverageSchema,
        summarised: coverageSchema,
    }),
    lessons: z.object({
        total: z.number().int().nonnegative(),
        titled: coverageSchema,
        summarised: coverageSchema,
        keyTopics: coverageSchema,
    }),
    segments: z.object({
        total: z.number().int().nonnegative(),
        titled: coverageSchema,
        summarised: coverageSchema,
    }),
    chunks: z.object({
        total: z.number().int().nonnegative(),
        embedded: coverageSchema,
    }),
    pathways: z.number().int().nonnegative(),
    skills: z.number().int().nonnegative(),
    /**
     * When each stage of the pipeline last wrote something, derived from the rows themselves.
     *
     * There is no ingest-run table — ingestion is a CLI operation and prompt 12 is explicitly not
     * allowed to build a job runner — so these are `max(updated_at)` and `max(embedded_at)` over
     * the tables each stage owns. That is a weaker claim than a run log and it is stated as one:
     * "the newest row this stage produced", not "the last run succeeded".
     */
    lastWrite: z.object({
        catalog: z.string().nullable().describe('ISO 8601; newest `courses.updated_at` (prompt 03)'),
        transcripts: z
            .string()
            .nullable()
            .describe('ISO 8601; newest `segments.updated_at` (prompt 04)'),
        enrichment: z.string().nullable().describe('ISO 8601; newest `lessons.updated_at` (prompt 05)'),
        courseEmbeddings: z.string().nullable().describe('ISO 8601 (prompt 06)'),
        chunkEmbeddings: z.string().nullable().describe('ISO 8601 (prompt 06)'),
    }),
    /**
     * The reports the ingestion CLI writes, as paths rather than as links.
     *
     * Deliberately not downloadable. Serving a file named in a response would make this endpoint a
     * file reader, and these are 63 KB of reconciliation detail written to a gitignored directory
     * that a deployed container does not even have — so `generatedAt` is null in Azure and that is
     * the honest answer, not a bug. What an admin needs from a dashboard is to know which report
     * covers the gap they are looking at and whether it is older than the data; the file itself is
     * read where ingestion runs.
     */
    reports: z.array(z.object({
        id: z.string(),
        label: z.string(),
        prompt: z.string().describe('Which prompt in the set generates it'),
        path: z.string().describe('Relative to the API process’s own DATA_DIR'),
        generatedAt: z
            .string()
            .nullable()
            .describe('ISO 8601; null when the file is not present under DATA_DIR, which is the expected ' +
            'answer in a deployed container and wherever the API runs from a different working ' +
            'directory than the ingestion CLI did'),
        bytes: z.number().int().nonnegative().nullable(),
    })),
    /**
     * Inconsistencies that silently degrade search, each with the count of rows affected.
     *
     * These are the failures that produce no error anywhere: an unembedded chunk is simply never
     * retrieved, and a course with no summary is retrieved less well than its neighbours. Nothing
     * here is a count of rows that are *allowed* to be null — the 74 courses without transcripts
     * are correct and are not an issue.
     */
    issues: z.array(z.object({
        code: z.string(),
        label: z.string(),
        count: z.number().int().nonnegative(),
        severity: z.enum(['warning', 'info']),
        detail: z.string(),
    })),
});
/* -- Query analytics ---------------------------------------------------------------------- */
/**
 * The filters, separate from the pagination, because the CSV export takes one and not the other.
 *
 * An export is of the filtered *set*, not of a page — so a route that accepted `limit` and then
 * ignored it would be lying in its own OpenAPI document. Splitting the schema is what makes
 * "same filters, no paging" expressible rather than merely intended.
 */
export const adminQueryFilterSchema = z.object({
    from: z.iso.datetime().optional().describe('ISO 8601 lower bound on created_at, inclusive'),
    to: z.iso.datetime().optional().describe('ISO 8601 upper bound on created_at, exclusive'),
    userId: identifierSchema.optional(),
    /** `true` keeps only degraded responses; `false` keeps only healthy ones. */
    degraded: z
        .enum(['true', 'false'])
        .transform((value) => value === 'true')
        .optional(),
    /**
     * Only the requests that cited nothing.
     *
     * The most valuable filter here: a question that returned no citations is either a gap in the
     * catalog or a failure of retrieval, and both are things worth fixing. Counted on citations
     * rather than on courses, because the degraded path returns cards with no citations and is a
     * different problem with a different fix.
     */
    zeroResult: z
        .enum(['true', 'false'])
        .transform((value) => value === 'true')
        .optional(),
});
export const adminQueryListQuerySchema = adminQueryFilterSchema.extend(adminPaginationShape);
/**
 * One logged request.
 *
 * `query` is what the person typed, which is the whole reason the table exists. What is *not*
 * here: the answer text, the retrieved snippets and the caller's IP. The first two are not
 * stored at all (see `search_queries`), and the third is stored but is not dashboard material.
 */
export const adminQueryRowSchema = z.object({
    id: z.string(),
    createdAt: z.string().describe('ISO 8601'),
    query: z.string(),
    userEmail: z.string().nullable().describe('Null for an anonymous or since-deleted user'),
    userId: z.string().nullable(),
    latencyMs: z.number().int().nonnegative(),
    toolCallCount: z.number().int().nonnegative(),
    iterations: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    citationCount: z.number().int().nonnegative(),
    /** Markers the model emitted for things no tool returned. A hallucination counter. */
    droppedCitations: z.number().int().nonnegative(),
    degraded: z.boolean(),
    truncated: z.boolean(),
    cached: z.boolean(),
    errorCode: z.string().nullable(),
    model: z.string().nullable(),
});
export const adminQueryListResponseSchema = z.object({
    items: z.array(adminQueryRowSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int(),
    offset: z.number().int(),
});
/**
 * The aggregates.
 *
 * Percentiles rather than a mean, because the mean of a latency distribution with a cache in
 * front of it describes nothing anybody experiences. p50 is the ordinary request; p95 is the one
 * that makes someone think the page is broken.
 */
export const adminQueryStatsSchema = z.object({
    totalQueries: z.number().int().nonnegative(),
    degradedQueries: z.number().int().nonnegative(),
    cachedQueries: z.number().int().nonnegative(),
    zeroResultQueries: z.number().int().nonnegative(),
    uniqueUsers: z.number().int().nonnegative(),
    p50LatencyMs: z.number().int().nonnegative(),
    p95LatencyMs: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    /**
     * US dollars, from the per-million rates in `env`.
     *
     * An estimate and labelled as one: it prices the tokens this table recorded at today's rates
     * and knows nothing about committed throughput, regional pricing or a prompt-cache discount.
     * It is here because "the search is expensive" is not a number anyone can act on.
     */
    estimatedCostUsd: z.number().nonnegative(),
    perDay: z.array(z.object({
        day: z.string().describe('ISO 8601 date, UTC'),
        queries: z.number().int().nonnegative(),
        degraded: z.number().int().nonnegative(),
        zeroResult: z.number().int().nonnegative(),
        p95LatencyMs: z.number().int().nonnegative(),
    })),
    topQueries: z.array(z.object({
        normalizedQuery: z.string(),
        count: z.number().int().nonnegative(),
        lastAskedAt: z.string(),
    })),
    /** The list worth reading: questions this catalog could not answer with a single citation. */
    zeroResultQueriesList: z.array(z.object({
        normalizedQuery: z.string(),
        count: z.number().int().nonnegative(),
        lastAskedAt: z.string(),
        degraded: z.boolean().describe('True when at least one of those attempts was degraded'),
    })),
});
//# sourceMappingURL=admin.js.map